import {useDateTime} from '@hconnect/tuc-processes/fields/dateTime/useDateTime'
import {useIsConfirmed} from '@hconnect/tuc-processes/fields/isConfirmed/useIsConfirmed'
import {useLoadSpacing} from '@hconnect/tuc-processes/fields/loadSpacing/useLoadSpacing'
import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {usePlusLoadExpected} from '@hconnect/tuc-processes/fields/plusLoad/usePlusLoadExpected'
import {useQuantity} from '@hconnect/tuc-processes/fields/quantity/useQuantity'
import {useRetarder} from '@hconnect/tuc-processes/fields/retarder/useRetarder'
import {
  useAdditionalNotes,
  useDriverInstructions,
  usePoNumber
} from '@hconnect/tuc-processes/fields/textField'
import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {useUnloadingMethod} from '@hconnect/tuc-processes/fields/unloadingMethod/useUnloadingMethod'
import {useUsage} from '@hconnect/tuc-processes/fields/usage/useUsage'
import {Close} from '@mui/icons-material'
import {Box, Typography, Button} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {Banner} from '../../orderIntake/components/shared/Banner'
import {RmcOrder, RmcOrderUpdateOptions} from '../../orderIntake/types/order'
import {OrderIntakeSite} from '../../orderIntake/types/OrderIntakeSite.types'
import {formatJobSiteInfo} from '../../orderIntake/utils/jobSiteUtils'

import {getEditableFieldLabels} from './bulkOrderEditConfig'
import {DiscardChangesDialog} from './components/DiscardChangesDialog'
import {OrderConfirmationSection} from './components/fields/Confirm/OrderConfirmationSection'
import {useQuote} from './components/fields/Quote/useQuote'
import {MaterialTestingField} from './components/fields/Testing/MaterialTestingField'
import {StepButton} from './components/StepButton'
import {useOrderEditSlotValidation} from './hooks/useOrderEditSlotValidation'
import {useRmcCountryFeatures} from './hooks/useRmcCountryFeatures'
import {useRmcOrderEditLogic} from './hooks/useRmcOrderEditLogic'
import {RmcOrderEditStepView, EditableFieldType, StepCompleteResult} from './RmcOrderEditStepView'
import {useOrderEditableFieldConfigurations} from './useOrderEditConfig'

interface RmcOrderEditProps {
  selectedOrder: RmcOrder
  onClose: () => void
  onOrderUpdate?: (formData: FieldsForm, options?: RmcOrderUpdateOptions) => void
  jobSite: OrderIntakeSite | null
}

enum EditView {
  OVERVIEW = 'overview',
  STEP_EDIT = 'step_edit'
}

const RmcOrderEdit: React.FC<RmcOrderEditProps> = ({
  selectedOrder,
  onClose,
  onOrderUpdate,
  jobSite
}) => {
  const {t} = useTranslation()
  const editableFieldLabels = getEditableFieldLabels(t)

  const regionFeatures = useRmcCountryFeatures()

  const [currentView, setCurrentView] = React.useState<EditView>(EditView.OVERVIEW)
  const [editingStepType, setEditingStepType] = React.useState<EditableFieldType | null>(null)
  const [showDiscardDialog, setShowDiscardDialog] = React.useState(false)

  const {canConfirmOrder, confirmationPinRequired} = useIsConfirmed()

  // Memoize confirmation section state to reduce complexity
  const confirmationState = React.useMemo(() => {
    const isDisabled = !canConfirmOrder || confirmationPinRequired
    const disabledMessage =
      !canConfirmOrder && !confirmationPinRequired
        ? 'orderIntake.rmc.orderManagement.confirmation.contactDispatch'
        : undefined
    return {isDisabled, disabledMessage, isPinRequired: confirmationPinRequired}
  }, [canConfirmOrder, confirmationPinRequired])

  const {quantitySelection} = useQuantity()
  const {plusLoad} = usePlusLoadExpected()
  const {selectedUnloadingMethod} = useUnloadingMethod()
  const {timeOnJobSite, spacingOrFlowRate} = useLoadSpacing()
  const {dateTime} = useDateTime()
  const {poNumber} = usePoNumber()
  const {driverInstructions} = useDriverInstructions()
  const {additionalNotes} = useAdditionalNotes()
  const {usage} = useUsage()
  const {selectedMaterial, mixCharacteristics: materialVariations} = useMix()
  const {retarder} = useRetarder()
  const {selectedQuote} = useQuote()

  const {orderEditSlotValidation} = useOrderEditSlotValidation({
    jobSite
  })

  const {
    hasUnsavedChanges,
    getStepDisplayValue,
    handleSubmitAndSave,
    handleDiscardChanges,
    retarderError,
    driverInstructionsError,
    additionalNotesError
  } = useRmcOrderEditLogic({
    orderId: selectedOrder.id,
    onOrderUpdate,
    onClose,
    fieldValues: {
      quote: selectedQuote?.quoteName,
      quantitySelection,
      plusLoad,
      unloadingMethod: selectedUnloadingMethod,
      timeOnJobSite,
      spacingOrFlowRate,
      dateTime,
      poNumber,
      driverInstructions,
      additionalNotes,
      usage,
      material: selectedMaterial ?? null,
      materialVariations,
      retarder
    },
    requiresDispatchApproval: orderEditSlotValidation.requiresDispatchApproval
  })

  // Get dynamic step configurations based on region features
  const editableFieldConfigs = useOrderEditableFieldConfigurations()

  // Handle close button click - check for unsaved changes
  const handleCloseClick = React.useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true)
    } else {
      onClose()
    }
  }, [hasUnsavedChanges, onClose])

  // Cancel discard and continue editing
  const handleCancelDiscard = React.useCallback(() => {
    setShowDiscardDialog(false)
  }, [])

  // Handle discard with dialog close
  const handleDiscardWithClose = React.useCallback(() => {
    handleDiscardChanges()
    setShowDiscardDialog(false)
  }, [handleDiscardChanges])

  // Helper function to navigate to step editing
  const handleStepEdit = React.useCallback((stepType: EditableFieldType) => {
    setEditingStepType(stepType)
    setCurrentView(EditView.STEP_EDIT)
  }, [])

  // Helper function to go back to overview
  const handleBackToOverview = React.useCallback(() => {
    setCurrentView(EditView.OVERVIEW)
    setEditingStepType(null)
  }, [])

  // Handler for step completion - decides what to do after a step is completed
  const handleStepComplete = React.useCallback((result: StepCompleteResult) => {
    // If Date step was completed and the date actually changed, automatically navigate to Time step
    if (result.stepType === EditableFieldType.DATE && result.hasChanges) {
      setEditingStepType(EditableFieldType.TIME)
      // Stay in STEP_EDIT view, just change the step type
      return
    }

    // Default behavior: go back to overview
    setCurrentView(EditView.OVERVIEW)
    setEditingStepType(null)
  }, [])

  // Render step edit view when navigating to a specific step
  if (currentView === EditView.STEP_EDIT && editingStepType) {
    return (
      <RmcOrderEditStepView
        stepType={editingStepType}
        onBack={handleBackToOverview}
        onStepComplete={handleStepComplete}
        onOrderUpdate={onOrderUpdate}
        jobSite={jobSite}
      />
    )
  }

  const shouldDisableSubmit = !!additionalNotesError || !!driverInstructionsError || !!retarderError // todo: unify validation

  // Main overview view with step buttons
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}
      data-test-id="order-edit-view"
    >
      {/* Header */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}
        >
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              color: '#29333d',
              fontFamily: 'Inter'
            }}
          >
            {t('orderIntake.rmc.orderManagement.editOrder', 'Edit order')}
          </Typography>
          <Button
            onClick={handleCloseClick}
            sx={{
              minWidth: 'auto',
              padding: '4px',
              color: '#52667a'
            }}
            data-test-id="close-order-edit-button"
          >
            <Close sx={{fontSize: '16px'}} />
          </Button>
        </Box>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            color: '#52667a',
            fontFamily: 'Inter'
          }}
        >
          {formatJobSiteInfo(jobSite)}
        </Typography>
      </Box>

      {/* Global slot validation banner for dispatch approval */}
      {orderEditSlotValidation.needsReselection && orderEditSlotValidation.error && (
        <Banner
          variant="warning"
          message={orderEditSlotValidation.error}
          marginTop="0px"
          marginBottom="0px"
          data-test-id="global-slot-validation-warning"
        />
      )}

      {/* Show info banner when slot validation is actually loading */}
      {orderEditSlotValidation.isLoading && (
        <Banner
          variant="info"
          message={t(
            'orderIntake.rmc.orderManagement.checkingSlotAvailability',
            'Checking slot availability with updated order parameters...'
          )}
          marginTop="0px"
          marginBottom="0px"
          data-test-id="slot-validation-checking"
        />
      )}

      {/* Confirmation Section - Only show if canConfirmOrder feature is enabled */}
      {regionFeatures.canConfirmOrder && (
        <OrderConfirmationSection
          disabled={confirmationState.isDisabled}
          disabledMessage={confirmationState.disabledMessage}
          isPinRequired={confirmationState.isPinRequired}
          data-test-id={`order-confirmation-section-${selectedOrder.id}`}
        />
      )}

      {/* Material Testing Field - only show if hasTesting is enabled */}
      {regionFeatures.hasTesting && (
        <MaterialTestingField data-test-id={`material-testing-field-${selectedOrder.id}`} />
      )}

      {/* Step Buttons */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
        {editableFieldConfigs.map((fieldConfig) => {
          const isDispatchApproval =
            fieldConfig.editStepType === EditableFieldType.TIME &&
            orderEditSlotValidation.requiresDispatchApproval

          return (
            <StepButton
              key={fieldConfig.editStepType}
              editStepType={fieldConfig.editStepType}
              label={editableFieldLabels[fieldConfig.editStepType]}
              value={getStepDisplayValue(fieldConfig.editStepType)}
              onClick={() => handleStepEdit(fieldConfig.editStepType)}
              isValid={true}
              error={''}
              requiresDispatchApproval={isDispatchApproval}
            />
          )
        })}
      </Box>

      {/* Action Buttons */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmitAndSave}
          disabled={shouldDisableSubmit}
          sx={{
            height: '48px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#ffffff',
            backgroundColor: !shouldDisableSubmit ? '#016ad4' : '#d1d9e0',
            fontFamily: 'Inter',
            '&:hover': {
              backgroundColor: !shouldDisableSubmit ? '#014ba8' : '#d1d9e0'
            },
            '&:disabled': {
              backgroundColor: '#d1d9e0',
              color: '#888'
            }
          }}
          data-test-id="final-review-button"
        >
          {!shouldDisableSubmit
            ? t('orderIntake.rmc.orderManagement.finalReview', 'Final Review')
            : t(
                'orderIntake.rmc.orderManagement.fixValidationErrors',
                'Fix validation errors to continue'
              )}
        </Button>
      </Box>

      {/* Discard Changes Confirmation Dialog */}
      <DiscardChangesDialog
        open={showDiscardDialog}
        onCancel={handleCancelDiscard}
        onDiscard={handleDiscardWithClose}
        data-test-id="discard-changes-dialog"
      />
    </Box>
  )
}

export {RmcOrderEdit}
